import React, { useEffect, useState } from 'react';
import { Modal } from '../Modal/Modal.component';
import {
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  InputLabel,
  OutlinedInput,
  FormHelperText,
} from '@material-ui/core';
import { useLayoutContext } from '../../Context/Layout.context';
import { updateUserPhone, updateUserEmail, updateUserEmailConfirmSms } from '../../services/users/users.service';
import Visibility from '@material-ui/icons/Visibility';
import VisibilityOff from '@material-ui/icons/VisibilityOff';
import { UpdateButton } from './UpdateButton.component';
import { useStyles } from './EditUserPhone.styles';
import { useTranslation } from 'react-i18next';
import { TwoFactorAuthResponse } from '../../services/users/users.types';
import { AxiosError, AxiosResponse } from 'axios';
import { useHistory } from 'react-router-dom';
import { Paths } from '../../utils/routerUrls';
import { TokenAuthUser, TokenAuthUserExpired } from './TokenAuthUser.component';

interface AuthUserProps {
  open: boolean;
  phone?: string;
  phoneHidden?: boolean;
  email?: string;
  userId: string;
  onClose(): void;
  onNavBack(): void;
  onCloseUpdate(): void;
  onEditUserMailClose?: () => void;
  onEditUserPhoneClose?: () => void;
  handleEmailAuthorisation?: () => void;
  onCloseXButton: () => void;
}

export const AuthUser: React.FC<AuthUserProps> = (props) => {
  const classes = useStyles();
  const { t } = useTranslation();

  const [password, setPassword] = useState<string>('');
  const [validPassword, setValidPassword] = useState<boolean>(true);
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [newInput, setIsNewInput] = useState<boolean>(false);
  const [isUpdateButtonDisabled, setIsUpdateButtonDisabled] = useState<boolean>(true);
  const [firstStepModalOn, setFirstStepModalOn] = useState<boolean>(true);
  const [secoundStepModalOn, setSecoundStepModalOn] = useState<boolean>(false);
  const [thirdStepModalOn, setThirdStepModalOn] = useState<boolean>(false);
  const [smsNumber, setSmsNumber] = useState<number | null>(null);
  const [leftAttemts, setLeftAttemts] = useState<number | null>(null);
  const [smsExpireDateTime, setSmsExpireDateTime] = useState<Date | null>(null);
  const [smsExpireSeconds, setSmsExpireSeconds] = useState<number | null>(null);
  const [token, setToken] = useState<string>('');
  const [tokenId, setTokenId] = useState<string>('');
  const [prevToken, setPrevToken] = useState<string>('');
  const [isTokenValid, setIsTokenValid] = useState<boolean>(true);

  const [timer, setTimer] = useState<number>(0);

  const { openAlert, showLoader } = useLayoutContext();

  const history = useHistory();

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleChangePassword = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPassword(event.target.value);
    setIsNewInput(true);
  };

  const clearInput = () => {
    setValidPassword(true);
    setIsUpdateButtonDisabled(true);
    setShowPassword(false);
    setPassword('');
    setThirdStepModalOn(false);
    setSecoundStepModalOn(false);
    setFirstStepModalOn(true);
    setPrevToken('');
  };

  const handleUpdateUserPhone = () => {
    showLoader(true, 'Weryfikuję dane');
    if (props.phone) {
      updateUserPhone(props.phone, props.phoneHidden, password)
        .then(
          () => {
            openAlert('success');
            clearInput();
            props.onClose();
            props.onNavBack();
          },
          () => {
            openAlert('save_error');
            setValidPassword(false);
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  const handleConfirmUpdate = () => {
    showLoader(true, 'Weryfikuję dane');
    if (props.email) {
      updateUserEmailConfirmSms(props.userId, props.email, token, tokenId)
        .then(
          () => {
            openAlert(
              'success',
              'Na nowy adres wysłano wiadomość e-mail. Postępuj zgodnie z instrukcją w treści maila.',
            );
            //sessionStorage.setItem('newEmail', props.email);
            clearInput();
            props.handleEmailAuthorisation && props.handleEmailAuthorisation();
            props.onCloseUpdate();
          },
          (error) => {
            if (error.response.status === 429) {
              localStorage.setItem('2FA', 'failed');
              history.push(Paths.logout);
            }
            setSmsExpireDateTime(new Date(error.response.data.smsExpireDateTime));
            setLeftAttemts(error.response.data.leftAttemts);
            setSmsNumber(error.response.data.smsNumber);
            setIsTokenValid(false);
            setPrevToken(token);
            setToken('');
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  const handleUpdateUserEmail = () => {
    showLoader(true, 'Weryfikuję dane');

    if (props.email) {
      updateUserEmail(props.userId, props.email, password, true)
        .then(
          (result: AxiosResponse<TwoFactorAuthResponse>) => {
            if (props.email && !result.data.twoAuthRequired) {
              openAlert(
                'success',
                'Na nowy adres wysłano wiadomość e-mail. Postępuj zgodnie z instrukcją w treści maila.',
              );
              sessionStorage.setItem('newEmail', props.email);
              clearInput();
              props.handleEmailAuthorisation && props.handleEmailAuthorisation();
              props.onCloseUpdate();
            } else {
              setSmsNumber(result.data.smsNumber);
              setLeftAttemts(result.data.leftAttemts);
              setSmsExpireDateTime(new Date(result.data.smsExpireDateTime));
              setSmsExpireSeconds(result.data.smsExpireSeconds);
              setFirstStepModalOn(false);
              setSecoundStepModalOn(true);
              setTokenId(result.data.tokenId);
              setTimer(result.data.smsExpireSeconds);
            }
          },
          (error: AxiosError) => {
            if (error.response?.status === 429) {
              localStorage.setItem('2FA', 'failed');
              history.push(Paths.logout);
            }
            if (navigator.onLine) {
              setValidPassword(false);
              openAlert('save_error');
            } else {
              openAlert('error', 'W wyniku braku połączenia z internetem zapisywanie danych nie powiodło się.');
            }
          },
        )
        .finally(() => {
          showLoader(false);
        });
    }
  };

  useEffect(() => {
    if (!secoundStepModalOn) return;
    if (timer > 0) {
      const intervalId = setInterval(() => {
        setTimer(timer - 1);
      }, 1000);
      return () => clearInterval(intervalId);
    } else {
      setThirdStepModalOn(true);
      setSecoundStepModalOn(false);
    }
  }, [timer]);

  useEffect(() => {
    if (password === '') {
      setValidPassword(true);
    }
  }, [password]);

  useEffect(() => {
    if (newInput && password !== '') {
      setIsUpdateButtonDisabled(false);
      setValidPassword(true);
    } else if (password === '' || !validPassword) {
      setIsUpdateButtonDisabled(true);
    } else setIsUpdateButtonDisabled(false);
  }, [newInput, password]);

  return (
    <>
      <Modal
        open={props.open && firstStepModalOn}
        size="xs"
        xCloseButton={() => {
          clearInput();
          props.onCloseXButton();
        }}
        autoHeight={true}
      >
        <Grid container className={classes.container} style={{ paddingBottom: '10px' }}>
          <Grid>
            <div className={classes.modalSubTitle}>{`W celu weryfikacji podaj swoje hasło do ${t(
              'global:shortNames.system',
            )}`}</div>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <FormControl variant="outlined">
              <InputLabel
                style={{ color: !validPassword ? '#BC0000' : '#2C2D3E' }}
                htmlFor="outlined-adornment-password"
              >
                Hasło
              </InputLabel>
              <OutlinedInput
                id="outlined-adornment-password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={handleChangePassword}
                fullWidth={true}
                error={!validPassword}
                endAdornment={
                  <InputAdornment position="end">
                    <IconButton
                      disableRipple
                      aria-label="włącz widoczność hasła"
                      onClick={handleClickShowPassword}
                      edge="end"
                    >
                      {!showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                }
                inputProps={{
                  style: {
                    width: '220px',
                    padding: '15.5px',
                    color: '#2C2D3E',
                    borderColor: !validPassword ? '#BC0000' : '#2C2D3E',
                  },
                }}
                label="Hasło"
              />
              <FormHelperText className={classes.errorMessagesContainer} error id="password-error" component={'div'}>
                {!validPassword && <div className={classes.errorMessage}>Podane hasło jest nieprawidłowe</div>}
              </FormHelperText>
            </FormControl>
          </Grid>
          <Grid className={classes.verifyButton} item xs={9}>
            <UpdateButton
              text="Zweryfikuj"
              isDisabled={isUpdateButtonDisabled}
              onClick={props.phone ? handleUpdateUserPhone : handleUpdateUserEmail}
            />
          </Grid>
        </Grid>
      </Modal>

      <TokenAuthUser
        open={props.open && secoundStepModalOn}
        token={token}
        prevToken={prevToken}
        smsNumber={smsNumber}
        timer={timer}
        leftAttemts={leftAttemts}
        smsExpireDateTime={smsExpireDateTime}
        setToken={setToken}
        onCancel={() => {
          clearInput();
          props.onCloseXButton();
        }}
        handleConfirmUpdate={handleConfirmUpdate}
      />

      <TokenAuthUserExpired
        open={props.open && thirdStepModalOn}
        onCancel={() => {
          clearInput();
          props.onCloseXButton();
        }}
        onConfirm={() => {
          handleUpdateUserEmail();
          setThirdStepModalOn(false);
          setSecoundStepModalOn(true);
        }}
      />
    </>
  );
};
